import React, { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';

import { usePostApiV5FirmsSampleClient } from '@liscio/api';
import { ScreenContent, ScreenWrapper } from '@liscio/ui';

import { DashboardMessages } from './components/DashboardMessages';
import { DashboardMyRequests } from './components/DashboardMyRequests';
import { WhatToDoToday } from './components/WhatToDoToday';
import type { PreselectedRecipient } from 'components/DesktopFileUpload/recipientAdapter';
import type { GuidedFileResumeState } from 'modules/dashboard/guidedFileIntelligence';

export interface DashboardDesktopViewProps {
  autoOpenFileIntelligence?: boolean;
  onAutoOpenConsumed?: () => void;
  resumedFileIntelligence?: GuidedFileResumeState;
}

const provisionedQueryClients = new WeakSet<QueryClient>();

/**
 * Desktop dashboard for firm users.
 *
 * Rendered when the `enableDashboardRedesign` LaunchDarkly flag is on and the
 * user is on a desktop viewport (see the DashboardView switch).
 *
 * See docs/PLG/dashboard for direction & architecture.
 */
export const DashboardDesktopView: React.FC<DashboardDesktopViewProps> = ({
  autoOpenFileIntelligence = false,
  onAutoOpenConsumed,
  resumedFileIntelligence,
}) => {
  const queryClient = useQueryClient();
  const [sampleRecipient, setSampleRecipient] =
    useState<PreselectedRecipient>();
  const { mutate: provisionSampleClient } = usePostApiV5FirmsSampleClient({
    mutation: {
      onSuccess: ({ account, contact }) => {
        setSampleRecipient({
          account,
          contact: {
            id: contact.id,
            uuid: contact.uuid,
            cpaUserId: contact.cpa_user_id,
            firstName: contact.first_name,
            lastName: contact.last_name,
            email: null,
          },
        });
      },
      onError: (error) => {
        Sentry.captureException(error, {
          tags: {
            feature: 'guided_file_intelligence',
            stage: 'sample_provisioning',
          },
        });
      },
    },
  });

  useEffect(() => {
    if (provisionedQueryClients.has(queryClient)) return;
    provisionedQueryClients.add(queryClient);
    provisionSampleClient();
  }, [provisionSampleClient, queryClient]);

  return (
    <ScreenWrapper>
      <ScreenContent>
        <WhatToDoToday
          autoOpenFileIntelligence={autoOpenFileIntelligence}
          onAutoOpenConsumed={onAutoOpenConsumed}
          resumedFileIntelligence={resumedFileIntelligence}
          sampleRecipient={sampleRecipient}
        />
        <DashboardMessages />
        <DashboardMyRequests />
      </ScreenContent>
    </ScreenWrapper>
  );
};

export default DashboardDesktopView;
